// Action Creator
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Constants
import { BackgroundToForegroundColorMap } from "@ms/planner-generic-controls/lib/local/constants/ColorPaletteConstants";
import { PlanTaskCardPlaceHolders } from "@ms/planner-generic-controls/lib/local/constants/StyleConstants";
import { projectManagerAgentId } from "@ms/uno-constants/lib/local/AgentConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { TaskGroupingOption } from "@ms/uno-constants/lib/local/GroupingConstants";
import { ViewName, ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
// Controls
import { Avatar, Tooltip, useAnnounce } from "@fluentui/react-components";
import { Unknown as UnknownIcon } from "@ms/planner-generic-controls/lib/local/icons";
import { AddTaskCard } from "@ms/planner-presentational-controls/lib/local/addTaskCard/AddTaskCard";
import { AddIconPlacement } from "@ms/planner-presentational-controls/lib/local/assignmentControl/AssignmentControl";
import { TaskBoardView } from "@ms/planner-presentational-controls/lib/local/boardView/TaskBoardView";
import { LazyGoalEditorPanel } from "@ms/planner-presentational-controls/lib/local/goalEditor/LazyGoalEditorPanel";
import { UserContainer } from "@ms/planner-presentational-controls/lib/local/userContainer/UserContainer";
import { TaskBoardViewPlaceHolder } from "@ms/planner-presentational-controls/lib/local/uxPlaceholders/taskBoardView/TaskBoardViewPlaceHolder";
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
// Container Controls
import { AssignmentControlContainer } from "../../controls/assignmentControl/AssignmentControlContainer";
import { StagedAssignmentControlContainer } from "../../controls/assignmentControl/StagedAssignmentControlContainer";
// Styles
import { PlannerLogo } from "@ms/planner-generic-controls/lib/local/icons/svgs/PlannerLogo";
import { usePlanTasksBoardViewStyles } from "./PlanTasksBoardView.styles";
// Utilities
import { getTimeZone } from "@ms/uno-utilities/lib/local/CalendarUtilities";
import { parseInitialsFromName } from "@ms/uno-utilities/lib/local/StringUtilities";
import { getCopilotWorkflowStatusLabel } from "../../utilities/AgentUtilities";
import { openTaskDetailsEditor } from "../../utilities/boardUtilities";
import { OperationStatus } from "@ms/planner-models/lib/local/client/operations/Enumerations";
import { ChildCollectionPermissions, EntityCollectionPermissions } from "@ms/planner-models/lib/local/client/permissions/Enumerations";
import { TaskAccessContext } from "@ms/planner-models/lib/local/client/planner/Enumerations";
import { Goal } from "@ms/planner-models/lib/local/client/planner/goal/Goal";
import { TaskKind } from "@ms/planner-models/lib/local/client/planner/task/ITask";
import { FormerMember } from "@ms/planner-models/lib/local/client/user/FormerUser";
import { FilterFields } from "@ms/planner-models/lib/local/constants/FilterConstants";
import { TasksWithoutGoalAssociationGroup } from "@ms/uno-constants/lib/local/GroupingConstants";
import { CopilotWorkflowUnifiedState } from "@ms/uno-constants/lib/local/WorkflowConstants";
// Hooks
import { useLoggers } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/useLoggers";
import { useActionCreators } from "../../hooks/useActionCreators";
import { Agent, useAgent } from "../../hooks/useAgent";
import { useConfigProvider } from "../../hooks/useConfigProvider";
import { useStores } from "../../hooks/useStores";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Strings
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { ContainerType, CustomFieldValueType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
import { isGroupableCustomFieldDefinition, resolveCustomFieldDefinitionIdFromGroupingOption } from "@ms/planner-models/lib/local/utilities/CustomFieldDefinitionUtilities";
import { isFlagPermissionEnabled } from "@ms/planner-models/lib/local/utilities/PermissionsUtilities";
import { renderCategoryHeaderImage } from "@ms/planner-presentational-controls/lib/local/utilities/LabelUtilities";
import { generateAssigneeViewColumns, generateBucketViewColumns, generateCategoryViewColumns, generateCustomFieldDefinitionViewColumns, generateDateViewColumns, generateGoalsViewColumnsWithOrder, generatePriorityViewColumns, generateProjectManagerWorkflowStateViewColumns, generateTaskStatusViewColumns } from "@ms/planner-presentational-controls/lib/local/utilities/taskBoard/TaskBoardColumnUtilities";
import { canAccessPlanViaContainers, getContainerMembersFromStores } from "@ms/planner-stores/lib/local/utilities/ContainerUtilities";
import { getChecklistItemPermissionsFromStores, getCustomFieldDefinitionPermissionsFromStores, getIsPlanCreationInProgress, getPlanPermissionsFromStores, getTaskPermissionsFromStores } from "@ms/planner-stores/lib/local/utilities/PermissionsUtilities";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { toViewIdString } from "@ms/uno-utilities/lib/local/ViewUtilities";
import React, { Suspense, useCallback, useRef, useState } from "react";
const COMPONENT_NAME = "PlanTasksBoardView";
/**
 * PlanTasksBoardView component - A functional component that displays plan tasks in a board view
 * This component uses Fluent v9 patterns and hooks from the container-controls package
 */ export const PlanTasksBoardView = ({ autoFocus, viewId, isSlimView })=>{
    const [appContextStore, bucketStore, driveItemStore, goalStore, permissionsStore, planStore, userStore, viewStore, taskStore, modalStore, modernGroupsStore, rosterStore, teamsChannelStore, todoStore, performanceCollectionStore, customFieldDefinitionStore] = useStores([
        Store.AppContext,
        Store.Bucket,
        Store.DriveItem,
        Store.Goal,
        Store.Permissions,
        Store.Plan,
        Store.User,
        Store.View,
        Store.Tasks,
        Store.Modal,
        Store.ModernGroups,
        Store.Roster,
        Store.TeamsChannel,
        Store.Todo,
        Store.PerformanceCollection,
        Store.CustomFieldDefinition
    ]);
    const [bucketAc, linksAc, modalAc, notificationAc, planAc, planViewAc, taskAc, userAc, viewTransformsAc, goalAc, customFieldDefinitionAc] = useActionCreators([
        ActionCreator.Bucket,
        ActionCreator.Links,
        ActionCreator.Modal,
        ActionCreator.Notification,
        ActionCreator.Plan,
        ActionCreator.PlanView,
        ActionCreator.Task,
        ActionCreator.User,
        ActionCreator.ViewTransforms,
        ActionCreator.Goal,
        ActionCreator.CustomFieldDefinition
    ]);
    const configProvider = useConfigProvider();
    const styles = usePlanTasksBoardViewStyles();
    const { engagementLogger, traceLogger } = useLoggers();
    const { announce } = useAnnounce();
    // Load agents
    const [projectManagerAgent] = useAgent([
        Agent.ProjectManager
    ]);
    const agents = [];
    if (projectManagerAgent) {
        agents.push(projectManagerAgent);
    }
    /**
     * Tracks whether the AddTaskCard is opened from the add task button
     * - true = when the "+ Add task" button is clicked to display AddTaskCard
     * - false = when AddTaskCard is closed or displayed via another way (ie. empty plan)
     */ const [isAddTaskCardOpenedFromAddTaskButton, setIsAddTaskCardOpenedFromAddTaskButton] = useState(false);
    /**
     * Indicates when a card is being dragged to a different classification (primary = Active task, secondary = completed tasks section)
     */ const isCardChangingCompletedSectionRef = useRef(false);
    /**
     * Tracks a pending agent assignment for the add task card.
     * When set, the agent will be assigned after the task is created.
     */ const [pendingAgentId, setPendingAgentId] = useState(null);
    /** The goal ID for the currently open GoalEditorPanel, or null when closed */ const [goalEditorPanelGoalId, setGoalEditorPanelGoalId] = useState(null);
    /** Ref to the goal column header element that opened the panel, for focus restoration on close */ const goalEditorInvokerRef = useRef(null);
    const openGoalEditorPanel = (goalId, invoker)=>{
        goalEditorInvokerRef.current = invoker;
        setGoalEditorPanelGoalId(goalId);
    };
    const closeGoalEditorPanel = ()=>{
        goalEditorInvokerRef.current?.focus();
        goalEditorInvokerRef.current = null;
        setGoalEditorPanelGoalId(null);
    };
    const editGoalField = useCallback((fieldName, newValue)=>{
        if (!goalEditorPanelGoalId) {
            return;
        }
        const latestGoal = goalStore.getGoal(goalEditorPanelGoalId).data;
        if (!latestGoal) {
            return;
        }
        const updatedGoal = new Goal({
            ...latestGoal,
            [fieldName]: newValue
        });
        goalAc.updateGoal(latestGoal, updatedGoal);
    }, // goalStore and goalAc are stable references from useStores/useActionCreators; only state deps needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        goalEditorPanelGoalId
    ]);
    const openGoalTaskSelectionPanel = useCallback((goalId)=>{
        modalAc.openModal({
            modalType: ModalType.GoalsTaskSelectionPanel,
            goalId,
            planId: viewId.partitionKey
        });
    }, [
        modalAc,
        viewId.partitionKey
    ]);
    const openDeleteGoalDialog = useCallback(()=>{
        if (!goalEditorPanelGoalId) {
            return;
        }
        modalAc.openModal({
            modalType: ModalType.DeleteGoal,
            goalId: goalEditorPanelGoalId
        });
        setGoalEditorPanelGoalId(null);
    }, [
        modalAc,
        goalEditorPanelGoalId
    ]);
    const viewPartitionKey = viewId.partitionKey;
    const currentPlan = planStore.getPlan(viewPartitionKey).data;
    const planCreationStatus = planStore.getCreatePlanFromTemplateOperationStatus(viewPartitionKey);
    const isPlanCreationInProgress = planCreationStatus === OperationStatus.Running;
    const currentViewRenderState = performanceCollectionStore.getViewRenderState(viewId);
    if (currentPlan === null || currentViewRenderState === ViewRenderState.None || isPlanCreationInProgress) {
        return /*#__PURE__*/ React.createElement(TaskBoardViewPlaceHolder, {
            numberOfTaskCardsPerColumn: PlanTaskCardPlaceHolders,
            showAddTaskButtonShimmer: true
        });
    }
    const enableCustomFields = configProvider().flights.EnableCustomFields;
    const bucketStoreResult = bucketStore.getBucketsForPlan(viewPartitionKey);
    const isToDoPlan = currentPlan.container.containerType === ContainerType.ToDo;
    const currentTaskGrouping = viewStore.getCurrentTaskGroupingState(viewId);
    const boardViewData = taskStore.getTasksForBoardView({
        name: ViewName.PlanPage,
        partitionKey: viewPartitionKey
    }, currentTaskGrouping);
    const categories = currentPlan.planView.categories;
    const currentUserId = appContextStore.getAppContext().userId ?? "";
    const containerUtilitiesStores = {
        userStore,
        modernGroupsStore,
        rosterStore,
        teamsChannelStore,
        todoStore,
        appContextStore,
        driveItemStore,
        planStore
    };
    const permissionsUtilitiesStores = {
        ...containerUtilitiesStores,
        permissionsStore
    };
    /**
     * Create an add task card control provider with the current information
     */ const addCardControlProvider = (columnValue, sortAfter, sortBefore, valueSetter, toggleAddCardControl)=>{
        return /*#__PURE__*/ React.createElement(AddTaskCard, {
            autoFocus: isAddTaskCardOpenedFromAddTaskButton,
            createTask: (task)=>{
                // Capture and clear pending agent assignment before creating the task
                const agentIdToAssign = pendingAgentId;
                if (agentIdToAssign) {
                    setPendingAgentId(null);
                }
                planViewAc.createTask(task, columnValue, sortAfter, sortBefore, currentTaskGrouping, agentIdToAssign ?? undefined);
            },
            planId: viewPartitionKey,
            taskKind: isToDoPlan ? TaskKind.ToDo : TaskKind.Planner,
            toggleAddCard: toggleAddCardControl,
            closeOnKeyPress: false,
            buckets: bucketStoreResult.data,
            defaultDueDate: undefined,
            hideSetAssignments: currentTaskGrouping === TaskGroupingOption.Assignee,
            hideSetBucket: currentTaskGrouping === TaskGroupingOption.Bucket,
            hideSetDueDate: currentTaskGrouping === TaskGroupingOption.DueDate,
            alertUser: (titleSelector)=>{
                notificationAc.showToast({
                    title: Strings[titleSelector],
                    intent: "error",
                    isHidden: true
                });
            },
            timezone: getTimeZone(),
            renderAssignmentControlContainer: (task, userAssignments, onUserAssignmentsChanged, customStyles, onPickerOpenChange)=>{
                return /*#__PURE__*/ React.createElement(StagedAssignmentControlContainer, {
                    task: task,
                    userAssignments: userAssignments,
                    onUserAssignmentsChanged: onUserAssignmentsChanged,
                    showPlaceholderMessage: true,
                    strings: {
                        assignControlLauncherText: Strings.TaskAssignments_AssignControlLauncherText
                    },
                    maxDisplayablePersonas: configProvider().settings.clientBasicLimits.maxDisplayablePersonasAddTaskCard,
                    customStyles: customStyles,
                    hideSingleAssigneeDetails: false,
                    addIconPlacement: AddIconPlacement.BeforeAssignees,
                    hideFacepileBar: false,
                    agentOverride: {
                        onAssignAgent: (agentId)=>setPendingAgentId(agentId),
                        onUnassignAgent: ()=>setPendingAgentId(null),
                        assignedAgent: pendingAgentId ? agents.find((a)=>a.id === pendingAgentId) ?? null : null
                    },
                    onPickerOpenChange: onPickerOpenChange
                });
            }
        });
    };
    // Generate the taskboard callbacks
    const taskBoardCallbacks = {
        valueSetter: (task, columnValue, sortAfter, sortBefore, sourceColumnValue)=>{
            // Special handling for Project Manager workflow state grouping
            if (currentTaskGrouping === TaskGroupingOption.ProjectManagerWorkflowState && columnValue !== sourceColumnValue) {
                if (columnValue === CopilotWorkflowUnifiedState.InProgress) {
                    // Dragging to in progress column - assign task to agent
                    announce(Strings.CopilotStatus_WorkingOnIt, {
                        polite: false
                    });
                    taskAc.assignTaskToAgent(task.id, projectManagerAgentId);
                } else if (columnValue === CopilotWorkflowUnifiedState.Unassigned) {
                    // Dragging to Unassigned column - unassign task from agent
                    taskAc.unassignTaskFromAgent(task.id, projectManagerAgentId);
                }
            } else if (// Special handling for PMA Assignee grouping
            currentTaskGrouping === TaskGroupingOption.Assignee && columnValue === projectManagerAgentId && columnValue !== sourceColumnValue) {
                // Dragging to project manager agent column - assign task to agent
                announce(Strings.CopilotStatus_WorkingOnIt, {
                    polite: false
                });
                taskAc.assignTaskToAgent(task.id, projectManagerAgentId);
            } else {
                // Default behavior for all other groupings
                planViewAc.updateTask(task, columnValue, sortAfter, sortBefore, sourceColumnValue, currentTaskGrouping, isCardChangingCompletedSectionRef.current, ()=>{
                    // Reset the ref after the update is completed
                    isCardChangingCompletedSectionRef.current = false;
                });
            }
        },
        secondaryClassifier: (task)=>{
            return currentTaskGrouping !== TaskGroupingOption.Status ? task.isComplete() : false;
        },
        secondaryValueSetter: (task, useSecondaryValue)=>{
            if (currentTaskGrouping !== TaskGroupingOption.Status) {
                completedSectionValueSetter(task, useSecondaryValue);
            }
        },
        secondarySectionTitleProvider: (isVisible)=>{
            return Strings.TaskBoardColumn_CompletedSectionText;
        },
        onColumnDrop: (columnValue, sortAfter, sortBefore)=>{
            if (currentTaskGrouping === TaskGroupingOption.Bucket) {
                planViewAc.reorderBucket(columnValue, sortAfter, sortBefore);
            }
        },
        addCardControlProvider
    };
    /**
     * Opens the task details editor
     * @param taskId Id of task to open details for
     * @param onDismiss Callback function called when TaskDetailsEditor is closed
     * @param tab Optional tab to open in the task editor
     */ const onOpenTaskDetailsEditor = async (taskId, onDismiss, tab)=>{
        await openTaskDetailsEditor(taskId, TaskAccessContext.Plan, modalStore, modalAc, onDismiss, tab);
    };
    /**
     * Callback when a card on a board with completed section is dropped
     * @param task The task that is being dropped
     * @param [useSecondaryValue] Indicates whether the card is dropped on secondary section or not
     */ const completedSectionValueSetter = (task, useSecondaryValue)=>{
        if (useSecondaryValue !== task.isComplete()) {
            // Set state to indicate that there is a change between Active section -> Completed Section only if it is changing between sections
            isCardChangingCompletedSectionRef.current = true;
        }
    };
    /**
     * Get the header image for the uncategorized bucket. An info icon with tooltip
     */ const renderUncategorizedBucketHeaderImage = ()=>{
        if (isToDoPlan) {
            return undefined;
        }
        return /*#__PURE__*/ React.createElement(Tooltip, {
            content: Strings.Bucket_NullBucketInfoIconText,
            relationship: "label",
            positioning: "above",
            withArrow: true
        }, /*#__PURE__*/ React.createElement("div", {
            role: "img"
        }, /*#__PURE__*/ React.createElement(UnknownIcon, null)));
    };
    /** Get the user by id
     * @param userId The user id
     */ const getUser = (userId)=>{
        return userStore.getUserById(userId).data;
    };
    /**
     * Called when user avatar card is loaded
     */ const onLoadAssigneeUser = (user)=>{
        const storeImageUrl = userStore.getUserPhoto(user.id).data;
        if (!storeImageUrl || storeImageUrl === FormerMember.Photo) {
            userAc.fetchPhotosForUsers([
                user.id
            ]);
        }
    };
    /**
     * Generate the header image for an assignee column
     * @param user IUser to generate the header image for
     */ const renderAssigneeHeaderImage = (user)=>{
        const imageUrl = userStore.getUserPhoto(user.id).data;
        return /*#__PURE__*/ React.createElement(UserContainer, {
            user: user,
            onLoad: onLoadAssigneeUser,
            containerId: `userContainer-assigneeHeader-${user.id}`
        }, /*#__PURE__*/ React.createElement(Avatar, {
            color: "colorful",
            name: user.displayName,
            initials: parseInitialsFromName(user.displayName),
            image: imageUrl ? {
                src: imageUrl,
                alt: user.displayName
            } : undefined
        }));
    };
    /**
     *  Get permissions for a plan
     */ const getPlanPermissions = ()=>{
        return getPlanPermissionsFromStores(currentPlan, permissionsUtilitiesStores, traceLogger);
    };
    /**
     * Render the header image for a project manager workflow column
     * @param columnPivotValue - The copilot workflow state to render image for
     * @returns JSX element containing the header image or null
     */ const ProjectManagerHeaderImage = ({ columnPivotValue })=>{
        const styles = usePlanTasksBoardViewStyles();
        if (columnPivotValue !== CopilotWorkflowUnifiedState.InProgress) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(PlannerLogo, {
            className: styles.projectManagerLogo
        });
    };
    /**
     * Render the header image for an agent column
     * @param agent IAgent to generate the header image for
     */ const renderAgentHeaderImage = (agent)=>{
        if (agent.id === projectManagerAgentId) {
            return /*#__PURE__*/ React.createElement(ProjectManagerHeaderImage, {
                columnPivotValue: CopilotWorkflowUnifiedState.InProgress
            });
        }
        return undefined;
    };
    let columnDefinitions = [];
    let columnCreationEnabled = false;
    const bucketPermissions = permissionsStore.getBucketPermissions(currentPlan, canAccessPlanViaContainers(currentPlan, permissionsUtilitiesStores), getIsPlanCreationInProgress(currentPlan.id, permissionsUtilitiesStores));
    const buildColumns = ()=>{
        switch(currentTaskGrouping){
            case TaskGroupingOption.Bucket:
                const planPermissions = getPlanPermissions();
                const planBuckets = bucketStoreResult.data;
                const enableBucketColors = configProvider().flights.EnableBucketColors;
                columnDefinitions = generateBucketViewColumns(boardViewData, planBuckets, true, isToDoPlan ? Strings.Bucket_NullBucketLabelForTodo : Strings.Bucket_NullBucketLabel, bucketPermissions, renderUncategorizedBucketHeaderImage, (originalBucket, updatedTitle)=>{
                    const updatedBucket = originalBucket.setProperty("displayName", updatedTitle);
                    bucketAc.updateBucket(originalBucket, updatedBucket);
                }, enableBucketColors && bucketPermissions.canUpdateColor ? (originalBucket, color)=>{
                    const updatedBucket = originalBucket.setBucketColor(color);
                    bucketAc.updateBucket(originalBucket, updatedBucket);
                } : undefined, enableBucketColors);
                columnCreationEnabled = isFlagPermissionEnabled(planPermissions.bucketPermissions, EntityCollectionPermissions.EnableAdd);
                break;
            case TaskGroupingOption.Status:
                columnDefinitions = generateTaskStatusViewColumns(boardViewData, true, false);
                break;
            case TaskGroupingOption.Priority:
                columnDefinitions = generatePriorityViewColumns(boardViewData, false);
                break;
            case TaskGroupingOption.Assignee:
                const storeUsers = getContainerMembersFromStores(currentPlan.container, containerUtilitiesStores, traceLogger);
                columnDefinitions = generateAssigneeViewColumns(boardViewData, storeUsers, getPlanPermissions().canUseAgents ? agents : [], false, appContextStore.getAppContext().userId ?? "", {} /** TODO: provide a map of agent IDs to their info content */ , getUser, renderAgentHeaderImage, renderAssigneeHeaderImage);
                break;
            case TaskGroupingOption.Category:
                columnDefinitions = generateCategoryViewColumns(boardViewData, viewPartitionKey, categories, true, configProvider().settings.clientBasicLimits.maxCategoryNameLength, planViewAc.updateCategoryTitle, (categoryIndex)=>renderCategoryHeaderImage(categories, categoryIndex));
                break;
            case TaskGroupingOption.DueDate:
                columnDefinitions = generateDateViewColumns(boardViewData, false, true);
                break;
            case TaskGroupingOption.FinishDate:
                columnDefinitions = generateDateViewColumns(boardViewData, false, true, undefined, undefined, TaskGroupingOption.FinishDate);
                break;
            case TaskGroupingOption.ProjectManagerWorkflowState:
                const canAssignToAgent = getPlanPermissions().canAssignAgents;
                columnDefinitions = generateProjectManagerWorkflowStateViewColumns(boardViewData, true, canAssignToAgent, (columnPivotValue)=>/*#__PURE__*/ React.createElement(ProjectManagerHeaderImage, {
                        columnPivotValue: columnPivotValue
                    }));
                break;
            case TaskGroupingOption.Goal:
                // Group-by-Goal must read from boardViewData (the filtered Board partition keyed by
                // goal id + TasksWithoutGoalAssociationGroup) so view-level filters apply.
                const planGoals = goalStore.getGoalsForPlan(currentPlan.id).data;
                // Narrow the raw filter values (string | number | boolean | DueDateCategory | null)
                // to the goal-id strings we expect for FilterFields.GoalIds — no forced casts.
                const goalFilterValues = viewStore.getCurrentTaskFilter(viewId)?.filters.find((f)=>f.field === FilterFields.GoalIds && f.active)?.values.filter((v)=>typeof v === "string");
                const selectedGoalIds = goalFilterValues && goalFilterValues.length > 0 ? new Set(goalFilterValues) : null;
                const isNoGoalSelected = !selectedGoalIds || selectedGoalIds.has("") || selectedGoalIds.has(TasksWithoutGoalAssociationGroup);
                const sortedGoals = [
                    ...planGoals
                ].sort((a, b)=>a.priority !== b.priority ? a.priority - b.priority : a.id.localeCompare(b.id));
                // Return [] for unselected goals: the board index keys multi-goal tasks under every
                // goalId, so this prevents them appearing under non-selected columns.
                const getTasksForGoalColumn = (goalId)=>selectedGoalIds && !selectedGoalIds.has(goalId) ? [] : boardViewData[goalId] ?? [];
                // Pass [] so the helper skips emitting its own "No goal" column; we prepend ours below.
                columnDefinitions = generateGoalsViewColumnsWithOrder([], false, sortedGoals, viewPartitionKey, (_planId, goalId)=>getTasksForGoalColumn(goalId), (goal)=>/*#__PURE__*/ React.createElement("h3", {
                        className: styles.goalColumnHeaderTitle
                    }, /*#__PURE__*/ React.createElement("button", {
                        className: styles.goalColumnHeaderTitleButton,
                        onClick: (event)=>openGoalEditorPanel(goal.id, event.currentTarget)
                    }, goal.displayName)));
                columnDefinitions.unshift({
                    pivotValue: "",
                    dataEntities: isNoGoalSelected ? boardViewData[TasksWithoutGoalAssociationGroup] ?? [] : [],
                    title: Strings.TasksBoardPage_ColumnNoGoal,
                    isOpen: true,
                    canEditTitle: false,
                    canDeleteColumn: false,
                    canMoveColumnRight: false,
                    canMoveColumnLeft: false,
                    isCollapsable: false,
                    isCollapsed: false,
                    disableCustomOrderCards: false,
                    canMoveTaskWithinSameColumn: false
                });
                columnCreationEnabled = isFlagPermissionEnabled(getPlanPermissions().goalPermissions, EntityCollectionPermissions.EnableAdd);
                break;
            case TaskGroupingOption.Plan:
            case TaskGroupingOption.None:
                traceLogger.logTrace(0x1e158143 /* tag_4fyfd */ , TraceLevel.Warning, "Invalid task grouping state for plan board view");
                break;
            default:
                if (enableCustomFields) {
                    const cfdId = resolveCustomFieldDefinitionIdFromGroupingOption(currentTaskGrouping);
                    if (cfdId) {
                        const customFieldDefinition = customFieldDefinitionStore.getCustomFieldDefinition(cfdId).data;
                        if (customFieldDefinition) {
                            if (isGroupableCustomFieldDefinition(customFieldDefinition.valueType)) {
                                // Color picker is only relevant for SingleChoiceText fields, not Date fields
                                let onUpdateChoiceColor;
                                if (customFieldDefinition.valueType === CustomFieldValueType.SingleChoiceText) {
                                    const enableChoiceColors = configProvider().flights.EnableBucketColors;
                                    const cfdPermissions = getCustomFieldDefinitionPermissionsFromStores(customFieldDefinition, currentPlan, permissionsUtilitiesStores, traceLogger);
                                    const canUpdateChoices = isFlagPermissionEnabled(cfdPermissions.customFieldChoicePermissions, ChildCollectionPermissions.EnableUpdateItems);
                                    if (enableChoiceColors && canUpdateChoices) {
                                        onUpdateChoiceColor = (choiceId, color)=>{
                                            const foregroundColor = color != null ? BackgroundToForegroundColorMap[color] : undefined;
                                            const colorData = color != null && foregroundColor != null ? {
                                                backgroundColor: color,
                                                foregroundColor
                                            } : null;
                                            const updatedChoices = customFieldDefinition.choiceOptions.map((choice)=>choice.id === choiceId ? choice.setChoiceColor(colorData) : choice);
                                            const updatedCfd = customFieldDefinition.setProperty("choiceOptions", updatedChoices);
                                            customFieldDefinitionAc.updateCustomFieldDefinition(customFieldDefinition, updatedCfd);
                                        };
                                    }
                                }
                                columnDefinitions = generateCustomFieldDefinitionViewColumns(boardViewData, customFieldDefinition, false, onUpdateChoiceColor);
                            } else {
                                traceLogger.logTrace(0x1e008000 /* tag_4aiaa */ , TraceLevel.Warning, "Unsupported custom field type for task grouping: " + customFieldDefinition.valueType);
                            }
                        } else {
                            traceLogger.logTrace(0x1e006605 /* tag_4agyf */ , TraceLevel.Error, "Custom field definition not found: " + currentTaskGrouping);
                        }
                    } else {
                        traceLogger.logTrace(0x1e15735c /* tag_4fxn2 */ , TraceLevel.Error, "Unreachable case hit in task board column generation: " + currentTaskGrouping);
                    }
                } else {
                    traceLogger.logTrace(0x1d81d158 /* tag_363fy */ , TraceLevel.Error, "Unreachable case hit in task board column generation: " + currentTaskGrouping);
                }
                break;
        }
    };
    buildColumns();
    const goalForPanel = goalEditorPanelGoalId ? goalStore.getGoal(goalEditorPanelGoalId).data : null;
    /**
     * Get permissions for a task.
     * @param task Task
     * @returns Task permissions
     */ const getTaskPermissions = (task)=>{
        return getTaskPermissionsFromStores(task, planStore.getPlan(task.planId).data, permissionsUtilitiesStores, traceLogger);
    };
    const getChecklistPermissions = (task)=>{
        const plan = planStore.getPlan(task.planId).data;
        return getChecklistItemPermissionsFromStores(task, plan, permissionsUtilitiesStores, traceLogger);
    };
    /**
     * Check if copy is disabled by sensitivity label
     * @param task Task to check
     * @returns True if copy is disabled by sensitivity label
     */ const getDisableCopyTaskBySensitivity = (task)=>{
        return !getTaskPermissions(task).canCopy;
    };
    const taskBoardCardCallbacks = {
        fetchPlanData: (planId)=>{
            planAc.fetchPlan(planId);
        },
        getTaskPlan: (planId)=>{
            return planStore.getPlan(planId).data;
        },
        getLabelUsageCountsForPlan: configProvider().flights.EnableUsageBasedLabels ? (planId)=>taskStore.getLabelUsageCountsForPlan(planId) : undefined,
        getTaskBucket: (bucketId)=>{
            return bucketStore.getBucket(bucketId).data;
        },
        taskCallbacks: {
            onUpdateTask: (originalTask, updatedTask)=>{
                engagementLogger.logEngagement(COMPONENT_NAME, EngagementTelemetryConstants.UpdateTask);
                taskAc.updateTask(originalTask, updatedTask);
            },
            getBuckets: (planId)=>{
                return bucketStore.getBucketsForPlan(planId).data;
            },
            openModal: modalAc.openModal,
            onCopyLinkToTask: taskAc.copyLinkToTask,
            toggleMyDayForTask: taskAc.toggleMyDayForTask,
            getUser,
            getUserPhoto: (userId)=>{
                return userStore.getUserPhoto(userId).data;
            },
            onLoadAssigneeUser,
            getCopilotWorkflowStatusLabel: (taskId)=>{
                const task = taskStore.getTask(taskId).data;
                if (!task) {
                    return null;
                }
                const canUseAgents = getPlanPermissionsFromStores(currentPlan, permissionsUtilitiesStores, traceLogger).canUseAgents;
                return getCopilotWorkflowStatusLabel(task, canUseAgents);
            },
            getGoalTitle: (goalId)=>{
                const goal = goalStore.getGoal(goalId).data;
                return goal?.displayName ?? null;
            },
            onTaskContextMenuOpen: (planId)=>{
                planAc.fetchPlanSensitivityUserRights(planId);
            },
            setLabelsCollapsedState: (isCollapsed)=>{
                viewTransformsAc.setCategoryLabelMode(TaskAccessContext.Plan, viewPartitionKey, isCollapsed);
            },
            getDocumentPreviewThumbnail: (groupId, itemUrl)=>{
                return driveItemStore.getDriveItemThumbnail(groupId, itemUrl).data;
            },
            fetchThumbnailsForTasksWithAttachments: (taskId)=>{
                taskAc.fetchTaskContentLinkThumbnails(taskId);
            },
            onOpenLink: (contentLink)=>{
                linksAc.openContentInHost(contentLink);
            },
            showInfoBanner: (notificationId)=>{
                notificationAc.showNotification({
                    notificationId,
                    messageType: "info"
                });
            },
            renderAssignmentControlContainer (task, customStyles) {
                return /*#__PURE__*/ React.createElement(AssignmentControlContainer, {
                    task: task,
                    addIconPlacement: AddIconPlacement.BeforeAssignees,
                    hideFacepileBar: false,
                    hideSingleAssigneeDetails: true,
                    strings: undefined,
                    showPlaceholderMessage: false,
                    maxDisplayablePersonas: configProvider().settings.clientBasicLimits.maxDisplayablePersonasTaskCard,
                    customStyles: customStyles
                });
            }
        }
    };
    /** Delete a task, trigger dialog for recurring tasks, giving option to delete series
     * @param taskId The id of the task to delete
     */ const deleteTask = (taskId)=>{
        const task = taskStore.getTask(taskId).data;
        if (task) {
            if (task.recurrence?.isActive()) {
                engagementLogger.logEngagement(COMPONENT_NAME, EngagementTelemetryConstants.DeleteRecurringTaskDialogOpened);
                const deleteRecurringTaskModalData = {
                    modalType: ModalType.DeleteRecurringTask,
                    taskId: taskId,
                    onDismiss: modalAc.closeModal
                };
                modalAc.openModal(deleteRecurringTaskModalData);
            } else {
                taskAc.deleteTask(task.id);
            }
        }
    };
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(TaskBoardView, {
        autoFocus: autoFocus,
        key: toViewIdString(viewId),
        columnCreationEnabled: columnCreationEnabled,
        createColumnCallback: (displayName)=>{
            if (currentTaskGrouping === TaskGroupingOption.Bucket) {
                planViewAc.createBucket(displayName, viewPartitionKey);
            }
        },
        createGoalColumnCallback: (displayName, finishDate)=>{
            planViewAc.createGoal(displayName, viewPartitionKey, finishDate);
        },
        emptyBoardTitle: Strings.TaskBoardView_EmptyBoardTitle,
        emptyBoardDescription: Strings.TaskBoardView_EmptyBoardDescription,
        columnDefinitions: columnDefinitions,
        enableColumnMove: currentTaskGrouping === TaskGroupingOption.Bucket,
        useSecondaryClassification: currentTaskGrouping !== TaskGroupingOption.Status,
        callbacks: taskBoardCallbacks,
        taskBoardCardCallbacks: taskBoardCardCallbacks,
        onOpenAddTaskCardFromAddTaskButton: ()=>{
            notificationAc.showToast({
                title: Strings.SharedComponents_AddNewTaskButtonExpanded,
                intent: "info",
                isHidden: true
            });
            setIsAddTaskCardOpenedFromAddTaskButton(true);
        },
        onCloseAddTaskCard: ()=>{
            notificationAc.showToast({
                title: Strings.SharedComponents_AddNewTaskButtonCollapsed,
                intent: "info",
                isHidden: true
            });
            setIsAddTaskCardOpenedFromAddTaskButton(false);
        },
        showPlanTitleOnCard: false,
        showCopyLinkToTask: true,
        openTaskDetails: onOpenTaskDetailsEditor,
        currentUserId: currentUserId,
        disableColumnChanges: false,
        bucketVirtualization: true,
        isSlimView: isSlimView,
        enableAutoScrollOnDragItem: true,
        enableDragToScroll: true,
        currentTaskGrouping: currentTaskGrouping,
        onDeleteTask: deleteTask,
        getTaskPermissions: getTaskPermissions,
        getChecklistPermissions: getChecklistPermissions,
        getDisableCopyTaskBySensitivity: getDisableCopyTaskBySensitivity,
        getPlanPermissions: getPlanPermissions,
        enableCopyPaste: configProvider().flights.EnableCanvasGridTaskCopyPaste,
        enablePercentComplete: configProvider().flights.EnablePercentComplete
    }), goalForPanel && goalEditorPanelGoalId && /*#__PURE__*/ React.createElement(Suspense, {
        fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
    }, /*#__PURE__*/ React.createElement(LazyGoalEditorPanel, {
        currentGoal: goalForPanel,
        isReadOnly: !isFlagPermissionEnabled(getPlanPermissions().goalPermissions, EntityCollectionPermissions.EnableAdd),
        taskCount: taskStore.getTasksForGoal(viewPartitionKey, goalEditorPanelGoalId).length,
        maxNotesLength: configProvider().settings.clientBasicLimits.maxNotesLength,
        currentLocale: appContextStore.getAppContext().locale,
        onDismiss: closeGoalEditorPanel,
        editGoalField: editGoalField,
        openTaskSelectionPanel: openGoalTaskSelectionPanel,
        openDeleteGoalDialog: openDeleteGoalDialog,
        getUserById: getUser
    })));
};
